import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    User,
    Mail,
    Phone,
    Building,
    MapPin,
    Globe
} from 'lucide-react';
import {
    Container,
    Typography,
    TextField,
    Button,
    Grid,
    Box,
    IconButton,
    Select,
    MenuItem,
    InputAdornment,
    Avatar,
    Alert,
    Snackbar,
    useMediaQuery,
    useTheme,
    CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import Sidebar from '../Sidebar.js';
import styles from './EditPersonalInfo.module.css'; // Import CSS module

// Import service
import { authService } from '../../../services/authService';

// Define theme colors to use throughout the component
const themeColors = {
    primary: '#FF4775',
    primaryLight: '#FF7676',
    primaryGradient: 'linear-gradient(45deg, #FF4775 30%, #FF7676 90%)',
    primaryGradientHover: 'linear-gradient(45deg, #FF5585 30%, #FF8686 90%)',
    text: '#333333',
    textSecondary: '#666666',
    lightGray: '#f5f5f5',
    border: '#e0e0e0',
    borderHover: '#999999',
    white: '#FFFFFF'
};

const EditPersonalInfo = () => {
    const navigate = useNavigate();
    const { user, logout, getAccessTokenSilently, isLoading: authLoading } = useAuth0();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Let Sidebar component handle its own state
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [darkMode, setDarkMode] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(true);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States'
    });

    // 📱 Phone number formatting function
    const formatPhoneNumber = (value) => {
        // Remove all non-digit characters
        const phoneNumber = value.replace(/\D/g, '');
        
        // Don't format if empty
        if (!phoneNumber) return '';
        
        // Format as XXX-XXX-XXXX
        if (phoneNumber.length <= 3) {
            return phoneNumber;
        } else if (phoneNumber.length <= 6) {
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
        } else if (phoneNumber.length <= 10) {
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
        } else {
            // Limit to 10 digits max for US numbers
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
        }
    };

    // Enhanced input change handler that handles both regular fields and phone formatting
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'phone') {
            // Special handling for phone number - auto-format as user types
            const formattedPhone = formatPhoneNumber(value);
            setFormData(prevData => ({
                ...prevData,
                [name]: formattedPhone
            }));
        } else {
            // Regular handling for other fields
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    // Fetch full user profile data on component mount
    useEffect(() => {
        const fetchUserProfile = async () => {
            if (!user) return;

            try {
                setLoadingProfile(true);

                // Get authentication token
                const token = await getAccessTokenSilently({
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    scope: 'openid profile email'
                });

                // Get user profile data from backend
                console.log('Fetching user profile data...');
                const response = await authService.getUserProfile(token);
                console.log('Profile data response:', response);

                // If successful, update form with profile data
                if (response && response.data && response.data.success && response.data.data) {
                    const profileData = response.data.data;

                    // Debug: Check what values we're actually getting from the backend
                    console.log('Loading values from backend:');
                    console.log('- first_name:', JSON.stringify(profileData.first_name));
                    console.log('- last_name:', JSON.stringify(profileData.last_name));

                    // Check if we have valid values from backend
                    const hasValidFirstName = profileData.first_name &&
                        typeof profileData.first_name === 'string' &&
                        profileData.first_name.trim().length > 0;

                    const hasValidLastName = profileData.last_name &&
                        typeof profileData.last_name === 'string' &&
                        profileData.last_name.trim().length > 0;

                    console.log('Using backend values:', {
                        firstName: hasValidFirstName,
                        lastName: hasValidLastName
                    });

                    // Get Auth0 values as fallbacks
                    const auth0FirstName = user.name?.split(' ')[0] || '';
                    const auth0LastName = user.name?.split(' ').slice(1).join(' ') || '';

                    // Choose the appropriate values
                    const finalFirstName = hasValidFirstName ? profileData.first_name : auth0FirstName;
                    const finalLastName = hasValidLastName ? profileData.last_name : auth0LastName;

                    console.log('Final values to use:', {
                        firstName: finalFirstName,
                        lastName: finalLastName
                    });

                    setFormData({
                        firstName: finalFirstName,
                        lastName: finalLastName,
                        email: profileData.email || user.email || '',
                        phone: profileData.phone_number || '',
                        address1: profileData.address_line_1 || '',
                        address2: profileData.address_line_2 || '',
                        city: profileData.city || '',
                        state: profileData.state || '',
                        zip: profileData.zip_code || '',
                        country: profileData.country || 'United States'
                    });
                    console.log(`Loaded profile data with name: ${finalFirstName} ${finalLastName}`);
                } else {
                    // Fallback to basic user info if API doesn't return profile
                    console.warn("API response didn't contain valid profile data");
                    setFormData(prevData => ({
                        ...prevData,
                        firstName: user.name?.split(' ')[0] || '',
                        lastName: user.name?.split(' ').slice(1).join(' ') || '',
                        email: user.email || '',
                    }));
                    console.log("Using fallback profile data");
                }
            } catch (error) {
                console.error('Error fetching user profile:', error);
                console.error('Error response:', error.response?.data);
                // Still initialize with basic user info on error
                setFormData(prevData => ({
                    ...prevData,
                    firstName: user.name?.split(' ')[0] || '',
                    lastName: user.name?.split(' ').slice(1).join(' ') || '',
                    email: user.email || '',
                }));
            } finally {
                setLoadingProfile(false);
            }
        };

        fetchUserProfile();
    }, [user, getAccessTokenSilently]);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // Add a method for checking request content
    const modifyRequestForBackend = (data) => {
        // Create a shallow copy
        const modifiedData = { ...data };

        // Delete any empty values to reduce payload size
        Object.keys(modifiedData).forEach(key => {
            if (modifiedData[key] === '' || modifiedData[key] === null) {
                delete modifiedData[key];
            }
        });

        return modifiedData;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        setUpdateError('');
        setSuccessMessage('');

        // Verify required fields
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            setUpdateError('First name and last name are required');
            setIsUpdating(false);
            return;
        }

        try {
            // Get authentication token
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });

            console.log('Submitting profile update...');

            // Check if email has changed - special handling required
            if (formData.email !== user.email) {
                console.log('Email changed, special handling required');
                const confirmed = window.confirm(
                    'Changing your email will require re-verification. You will be logged out after the change. Continue?'
                );

                if (!confirmed) {
                    setIsUpdating(false);
                    return;
                }

                // Use authService to update email
                await authService.updateUserEmail({ email: formData.email }, token);

                setSuccessMessage('Email updated successfully. Please check your new email for verification.');
                setSnackbarOpen(true);

                // Log out after delay
                setTimeout(() => {
                    logout({ returnTo: window.location.origin });
                }, 3000);
                return;
            }

            // Handle other profile updates by mapping form fields to expected API format
            let profileData = {
                // IMPORTANT: Backend expects these exact field names
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone_number: formData.phone,
                address_line_1: formData.address1,
                address_line_2: formData.address2,
                city: formData.city,
                state: formData.state,
                zip_code: formData.zip,
                country: formData.country
                // Don't include profile_photo at all
            };

            // Remove any empty fields from the request
            profileData = modifyRequestForBackend(profileData);

            console.log('Updating profile with:', profileData);

            // Call updateUserProfile API using PUT method 
            const response = await authService.updateUserProfile(profileData, token);
            console.log('Full profile update response:', response);

            if (response && response.data) {
                console.log('Response data:', response.data);

                if (response.data.success) {
                    console.log('Updated profile in response:', response.data.data);
                    setSuccessMessage('Profile updated successfully');
                    setSnackbarOpen(true);

                    // Optionally update form data with values from server response
                    if (response.data.data) {
                        const updatedProfile = response.data.data;
                        setFormData({
                            firstName: updatedProfile.first_name || '',
                            lastName: updatedProfile.last_name || '',
                            email: updatedProfile.email || user.email || '',
                            phone: updatedProfile.phone_number || '',
                            address1: updatedProfile.address_line_1 || '',
                            address2: updatedProfile.address_line_2 || '',
                            city: updatedProfile.city || '',
                            state: updatedProfile.state || '',
                            zip: updatedProfile.zip_code || '',
                            country: updatedProfile.country || 'United States'
                        });
                    }
                } else {
                    throw new Error(response.data.message || 'Failed to update profile');
                }
            } else {
                throw new Error('Invalid response format');
            }

        } catch (error) {
            console.error('Error updating profile:', error);

            // Try to extract more useful error message if possible
            let errorMessage = 'Failed to update profile';

            // Check if there's a detailed error message in the response HTML
            if (error.response && error.response.data) {
                console.error('Error response data:', error.response.data);

                if (typeof error.response.data === 'string') {
                    if (error.response.data.includes('profile_photo')) {
                        errorMessage = 'Error with profile photo. Profile update service is temporarily unavailable.';
                    } else if (error.response.data.includes('exception_value')) {
                        // Try to extract the actual error message from Django's error page
                        const match = error.response.data.match(/<pre class="exception_value">(.*?)<\/pre>/);
                        if (match && match[1]) {
                            errorMessage = `Server error: ${match[1].replace(/&#x27;/g, "'").trim()}`;
                        }
                    }
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
            } else if (error.friendlyMessage) {
                errorMessage = error.friendlyMessage;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setUpdateError(errorMessage);

            // Reset email to original on error
            if (formData.email !== user?.email) {
                setFormData(prev => ({
                    ...prev,
                    email: user.email
                }));
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Common styles for input fields
    const inputFieldStyles = {
        backgroundColor: 'white',
        color: themeColors.text,
        "& .MuiOutlinedInput-root": {
            backgroundColor: 'white',
            color: themeColors.text,
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary
            }
        }
    };

    // Display loading state if still loading profile data
    if (loadingProfile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}
            >
                <CircularProgress sx={{ color: themeColors.primary }} />
            </Box>
        );
    }

    return (
        <Box
            className={styles.container}
            sx={{
                display: 'flex',
                minHeight: '100vh',
                bgcolor: 'grey.50',
            }}
        >
            {/* Use the Sidebar component which handles its own responsiveness */}
            <Sidebar
                onToggle={toggleSidebar}
                darkMode={darkMode}
            />

            <Box
                component={motion.div}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                sx={{
                    flexGrow: 1,
                    ml: !isMobile && sidebarOpen ? '240px' : !isMobile ? '80px' : 0,
                    transition: 'margin-left 0.3s',
                    p: isMobile ? 2 : isTablet ? 2.5 : 3,
                    width: '100%'
                }}
            >
                <Container
                    maxWidth="md"
                    sx={{
                        mt: 4,
                        px: isMobile ? 2 : 3
                    }}
                >
                    {/* Error Alert */}
                    {updateError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {updateError}
                        </Alert>
                    )}

                    {/* Personal Information Form */}
                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: 3, mt: isMobile ? 3 : 0 }}>
                        Personal Information
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            {/* Name Fields */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <User size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ maxLength: 100 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <User size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ maxLength: 100 }}
                                />
                            </Grid>

                            {/* Contact Information */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    error={!!updateError}
                                    helperText={updateError || (
                                        formData.email !== user?.email ?
                                            "You'll need to verify your new email address" :
                                            ""
                                    )}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Mail size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    placeholder="123-456-7890"
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Phone size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ 
                                        maxLength: 12, // Length of "123-456-7890"
                                        inputMode: 'tel',
                                        autoComplete: 'tel'
                                    }}
                                    helperText="Auto-formatted as you type"
                                />
                            </Grid>

                            {/* Address Fields */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address Line 1"
                                    name="address1"
                                    value={formData.address1}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Building size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address Line 2"
                                    name="address2"
                                    value={formData.address2}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Building size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={isMobile ? 12 : 6} md={6}>
                                <TextField
                                    fullWidth
                                    label="City"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <MapPin size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={isMobile ? 6 : 3} md={3}>
                                <TextField
                                    fullWidth
                                    label="State"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <MapPin size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={isMobile ? 6 : 3} md={3}>
                                <TextField
                                    fullWidth
                                    label="ZIP Code"
                                    name="zip"
                                    value={formData.zip}
                                    onChange={handleInputChange}
                                    disabled={isUpdating}
                                    sx={inputFieldStyles}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <MapPin size={isMobile ? 18 : 20} color={themeColors.primary} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Select
                                    fullWidth
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    name="country"
                                    disabled={isUpdating}
                                    displayEmpty
                                    renderValue={(value) => value || "Select Country"}
                                    sx={{
                                        ...inputFieldStyles,
                                        backgroundColor: 'white',
                                        color: themeColors.text,
                                        "& .MuiSelect-select": {
                                            backgroundColor: 'white'
                                        }
                                    }}
                                    InputProps={{
                                        style: { backgroundColor: 'white', color: themeColors.text }
                                    }}
                                    IconComponent={() => (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: 'white',
                                            paddingRight: 1
                                        }}>
                                            <Globe size={isMobile ? 18 : 20} color={themeColors.primary} />
                                        </Box>
                                    )}
                                >
                                    <MenuItem value="United States">United States</MenuItem>
                                    <MenuItem value="Canada">Canada</MenuItem>
                                    <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                                    <MenuItem value="Australia">Australia</MenuItem>
                                    <MenuItem value="Germany">Germany</MenuItem>
                                    <MenuItem value="France">France</MenuItem>
                                    <MenuItem value="Japan">Japan</MenuItem>
                                    <MenuItem value="China">China</MenuItem>
                                    <MenuItem value="India">India</MenuItem>
                                </Select>
                            </Grid>
                        </Grid>

                        {/* Form Actions */}
                        <Box
                            className={styles.actions}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                flexDirection: isMobile ? 'column-reverse' : 'row',
                                gap: isMobile ? 2 : 3,
                                mt: isMobile ? 3 : 4
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/my-profile')}
                                disabled={isUpdating}
                                className={styles.cancelButton}
                                sx={{
                                    borderColor: themeColors.borderHover,
                                    color: themeColors.textSecondary,
                                    width: isMobile ? '100%' : 'auto'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isUpdating}
                                className={styles.saveButton}
                                variant="contained"
                                sx={{
                                    background: themeColors.primaryGradient,
                                    color: 'white',
                                    width: isMobile ? '100%' : 'auto',
                                    '&:hover': {
                                        background: themeColors.primaryGradientHover
                                    }
                                }}
                            >
                                {isUpdating ? (
                                    <>
                                        <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                        Saving...
                                    </>
                                ) : 'Save Changes'}
                            </Button>
                        </Box>
                    </form>
                </Container>
            </Box>

            {/* Success Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: isMobile ? 'center' : 'right'
                }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EditPersonalInfo;